import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import * as baseState from '../../../../../core/models/base-state/base-state';
import { PatientHealthConcern } from '../../../models/patient-health-concern';
import * as healthConcernActions from '../actions/patient-health-concern.actions';

export interface PatientHealthConcernsState extends EntityState<PatientHealthConcern> {
  baseState: baseState.BaseState;
  isLoaded: boolean;
  cachePatientId: number | null;
  cacheLoadedAt: number | null;
  detailLoading: boolean;
  detailError: unknown;
}

export const patientHealthConcernsAdapter = createEntityAdapter<PatientHealthConcern>({
  selectId: healthConcern => healthConcern.patientHealthConcernId,
});

export const initialState: PatientHealthConcernsState = patientHealthConcernsAdapter.getInitialState({
  baseState: baseState.createBaseState(undefined),
  isLoaded: false,
  cachePatientId: null,
  cacheLoadedAt: null,
  detailLoading: false,
  detailError: null,
});

export const patientHealthConcernsFeatureKey = 'patient-health-concerns';

export const patientHealthConcernsReducer = createReducer(
  initialState,

  on(
    healthConcernActions.loadPatientHealthConcerns,
    (state): PatientHealthConcernsState => ({
      ...state,
      baseState: {
        ...state.baseState,
        isLoading: true,
        error: null,
      },
    })
  ),

  on(
    healthConcernActions.loadPatientHealthConcernsSuccess,
    (state, { healthConcerns, patientId, loadedAt }): PatientHealthConcernsState => {
      const nextState = patientHealthConcernsAdapter.setAll(healthConcerns, state);

      return {
        ...nextState,
        baseState: {
          ...nextState.baseState,
          isLoading: false,
          error: null,
          totalRecordCount: healthConcerns.length,
        },
        isLoaded: true,
        cachePatientId: patientId,
        cacheLoadedAt: loadedAt,
      };
    }
  ),

  on(
    healthConcernActions.loadPatientHealthConcernsFailure,
    (state, { error }): PatientHealthConcernsState => ({
      ...state,
      baseState: {
        ...state.baseState,
        isLoading: false,
        error,
      },
    })
  ),

  on(
    healthConcernActions.loadPatientHealthConcernById,
    (state): PatientHealthConcernsState => ({
      ...state,
      detailLoading: true,
      detailError: null,
    })
  ),

  on(
    healthConcernActions.loadPatientHealthConcernByIdSuccess,
    (state, { healthConcern }): PatientHealthConcernsState => ({
      ...patientHealthConcernsAdapter.upsertOne(healthConcern, state),
      detailLoading: false,
      detailError: null,
    })
  ),

  on(
    healthConcernActions.loadPatientHealthConcernByIdFailure,
    (state, { error }): PatientHealthConcernsState => ({
      ...state,
      detailLoading: false,
      detailError: error,
    })
  ),

  on(
    healthConcernActions.invalidateHealthConcernsCache,
    (state): PatientHealthConcernsState => ({
      ...state,
      isLoaded: false,
      cachePatientId: null,
      cacheLoadedAt: null,
    })
  )
);
